import { forwardRef } from 'react'
import { TouchableOpacity } from 'react-native'

import { colors } from '@zet/design-system'
import { Stack, XStack } from '@zet/design-system/layout'
import { B1, B2, B5 } from '@zet/design-system/typography'
import { useUnsafeVariable } from 'nativewind'

import BankIcon from '../../assets/icons/BankIcon'
import CurrencyIcon from '../../assets/icons/CurrencyIcon'

interface FieldOption {
  optionValue: string
  optionId: any
  icon?: keyof typeof icons
}

interface Props {
  label?: string
  onChange: (value: any) => void
  onBlur: () => void
  isRequired?: boolean
  fieldOptions: FieldOption[]
  value: any
  errorMessage?: string
}

const icons = {
  BankIcon,
  CurrencyIcon,
}

type IconName = keyof typeof icons

const IconRenderer: React.FC<{
  iconName: IconName
  color: string
  size: number
}> = ({ iconName, color, size }) => {
  const IconComponent = icons[iconName]
  if (!IconComponent) {
    return <></>
  }
  return <IconComponent color={color} size={size} />
}

export const Toggle: React.FC<Props> = forwardRef(
  (
    { label, onChange, onBlur, value, errorMessage, isRequired, fieldOptions },
    ref
  ) => {
    const [colorPrimary100] = useUnsafeVariable('--color-primary-100')
    const [colorPrimary10] = useUnsafeVariable('--color-primary-10')

    return (
      <Stack>
        <XStack>
          {!!label && <B2 className="text-grays-80 mb-1">{label}</B2>}
          {isRequired && <B1 className="text-error-100 pl-1">*</B1>}
        </XStack>
        <XStack>
          {fieldOptions?.map((item) => (
            <TouchableOpacity
              onPress={() => {
                onChange(item.optionId)
                onBlur()
              }}
              key={item?.optionId}
            >
              <XStack
                style={{
                  borderColor:
                    value !== item?.optionId
                      ? colors.grays[40]
                      : colorPrimary100,
                  backgroundColor:
                    value !== item?.optionId ? '#fff' : colorPrimary10,
                }}
                className={`mr-3 items-center justify-between rounded-md border px-2 py-2`}
              >
                {item.icon && (
                  <IconRenderer
                    iconName={item.icon}
                    color={
                      value !== item.optionId
                        ? colors.grays[70]
                        : colorPrimary100
                    }
                    size={28}
                  />
                )}
                <B2
                  style={{
                    color:
                      value !== item.optionId
                        ? colors.grays[80]
                        : colorPrimary100,
                  }}
                  className={`${
                    value !== item.optionId
                      ? 'font-[Graphie-Regular]'
                      : 'font-[Graphie-SemiBold]'
                  } text-grays-80 ml-2`}
                >
                  {item.optionValue}
                </B2>
              </XStack>
            </TouchableOpacity>
          ))}
        </XStack>
        {!!errorMessage && <B5 className="text-error-100">{errorMessage}</B5>}
      </Stack>
    )
  }
)
